import React, { useMemo, useState } from 'react';
import styled from 'styled-components';
import { DateTime, Duration } from 'luxon';
import distance from '@turf/distance';
import bearing from '@turf/bearing';
import { UpOutlined, DownOutlined } from '@ant-design/icons';

import { type Strike, useLightningContext } from '@mono/lightning-context';
import { useSettingsContext } from '@mono/platmap-settings-context';
import type { LightningCircle } from '@mono/types/src/platmap';
import { convertDirectionToCardinal } from '@mono/direction-to-cardinal';

import { useTimeContext } from 'time-context';
import { lightningCircleContainsStrike } from './lightning-service-context/LightningCircle';
import { scaleLinear } from 'd3-scale';
import { isLightningLayer } from 'layerTypeUtils';

interface ChronologicalStrikesProps {
    maxHeight: number;
    theme: { background: string; text: string };
}
export const ChronologicalStrikes = ({ maxHeight, theme }: ChronologicalStrikesProps) => {
    const { settings } = useSettingsContext();
    const { strikes } = useLightningContext();
    const { selectedTime } = useTimeContext();

    const lightningLayer = settings.layers.find(isLightningLayer);
    const lightningCircles = lightningLayer?.state.type === 'lightning' ? lightningLayer.state.lightningCircles : undefined;
    const [expandedLightningLists, setExpandedLightningLists] = useState<string[]>(lightningCircles ? lightningCircles.map((lc) => lc.id) : []);

    const circleToStrikes = useMemo(() => {
        if (!selectedTime) return;
        if (!lightningCircles) return;

        return Object.fromEntries(
            lightningCircles.map((lightningCircle) => {
                const warningTime = DateTime.fromISO(selectedTime).toUTC().minus(Duration.fromISO(lightningCircle.warningAge)).toISO();
                const strikesWithinCircle = strikes.features
                    .filter((strike) => {
                        if (!lightningCircleContainsStrike(lightningCircle, strike)) return false;
                        if (strike.properties.dateTime < warningTime) return false;
                        return true;
                    })
                    .sort((a, b) => {
                        return a.properties.dateTime < b.properties.dateTime ? 1 : -1;
                    });

                return [
                    lightningCircle.id,
                    {
                        red: strikesWithinCircle.filter(
                            (s) => s.properties.dateTime > DateTime.fromISO(selectedTime).toUTC().minus(Duration.fromISO(lightningCircle.alertAge)).toISO()
                        ),
                        orange: strikesWithinCircle.filter((s) => {
                            if (s.properties.dateTime > DateTime.fromISO(selectedTime).toUTC().minus(Duration.fromISO(lightningCircle.alertAge)).toISO()) {
                                return false;
                            }
                            return true;
                        }),
                        mostRecentStrikeTime: strikesWithinCircle[0]?.properties.dateTime,
                    },
                ];
            })
        );
    }, [selectedTime]);

    if (!circleToStrikes) return <></>;
    if (!selectedTime) return <></>;
    if (!lightningLayer) return <></>;
    if (lightningLayer.state.type !== 'lightning') return <></>;
    if (!lightningCircles) return <></>;
    if (lightningCircles.length === 0) return <></>;

    return (
        <ChronologicalStrikesContainer style={{ color: theme.text, backgroundColor: theme.background, pointerEvents: 'all' }}>
            {/* Need to create the extra div because overflowY: scroll with border radius distorts text in chrome */}
            <div
                style={{
                    overflowY: 'scroll',
                    scrollbarWidth: 'none',
                    maxHeight: maxHeight - 64,
                }}
            >
                {lightningCircles.map((lightningCircle, i) => {
                    return (
                        <div
                            key={lightningCircle.id}
                            style={{
                                paddingTop: 6,
                                paddingLeft: 4,
                                paddingRight: 4,
                                paddingBottom: i === lightningCircles.length - 1 ? 8 : 0,
                                display: 'flex',
                                flexDirection: 'column',
                                gap: 4,
                            }}
                        >
                            {i !== 0 && (
                                <hr
                                    style={{
                                        borderTop: '1px solid #cdcdcd',
                                        borderBottom: 'none',
                                        borderLeft: 'none',
                                        borderRight: 'none',
                                        margin: 0,
                                        paddingBottom: 2,
                                        marginLeft: -4,
                                        marginRight: -4,
                                    }}
                                />
                            )}
                            <InteractiveHeader
                                style={{ fontSize: 14, fontWeight: 'bold', cursor: 'pointer' }}
                                onClick={() => {
                                    if (expandedLightningLists.includes(lightningCircle.id)) {
                                        setExpandedLightningLists(expandedLightningLists.filter((id) => id !== lightningCircle.id));
                                        return;
                                    }
                                    setExpandedLightningLists([...expandedLightningLists, lightningCircle.id]);
                                }}
                            >
                                <div
                                    style={{
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        alignItems: 'center',
                                        gap: 8,
                                        position: 'relative',
                                        width: '100%',
                                        color: theme.text,
                                    }}
                                >
                                    <div
                                        style={{
                                            opacity: circleToStrikes[lightningCircle.id]?.mostRecentStrikeTime ? 1 : 0.6,
                                            maxWidth: 240,
                                            overflow: 'hidden',
                                        }}
                                    >
                                        {lightningCircle.label ?? 'Lightning strikes'}
                                    </div>
                                    <div style={{ justifySelf: 'flex-end', display: 'flex', alignItems: 'center', maxHeight: 22 }}>
                                        {circleToStrikes[lightningCircle.id]?.mostRecentStrikeTime && (
                                            <LightningStrike
                                                fill={ageToColour(
                                                    DateTime.fromISO(selectedTime)
                                                        .toUTC()
                                                        .diff(DateTime.fromISO(circleToStrikes[lightningCircle.id].mostRecentStrikeTime).toUTC(), 'minutes')
                                                        .minutes
                                                )}
                                            />
                                        )}
                                        <IconButton
                                            style={{ opacity: circleToStrikes[lightningCircle.id]?.mostRecentStrikeTime ? 1 : 0.7 }}
                                            onClick={() => {
                                                expandedLightningLists.includes(lightningCircle.id)
                                                    ? setExpandedLightningLists(expandedLightningLists.filter((i) => i !== lightningCircle.id))
                                                    : setExpandedLightningLists([...expandedLightningLists, lightningCircle.id]);
                                            }}
                                        >
                                            {expandedLightningLists.includes(lightningCircle.id) ? <UpOutlined /> : <DownOutlined />}
                                        </IconButton>
                                    </div>
                                </div>
                            </InteractiveHeader>
                            {expandedLightningLists.includes(lightningCircle.id) && !circleToStrikes[lightningCircle.id]?.mostRecentStrikeTime && (
                                <div style={{ textAlign: 'center', opacity: 0.6 }}>No lightning within detection circle</div>
                            )}
                            {expandedLightningLists.includes(lightningCircle.id) && circleToStrikes[lightningCircle.id]?.mostRecentStrikeTime && (
                                <LightningList>
                                    <div
                                        id='platmap-lightning-list'
                                        style={{
                                            display: 'flex',
                                            flexDirection: 'row',
                                            fontSize: 12,
                                            fontWeight: 'bold',
                                            gap: 8,
                                            paddingBottom: 12,
                                            letterSpacing: 0.4,
                                        }}
                                    >
                                        <div style={{ width: 50 }}>TIME</div>
                                        <div style={{ width: 75 }}>TYPE</div>
                                        <div style={{ width: 75, textAlign: 'right' }}>DISTANCE</div>
                                        <div style={{ width: 69, textAlign: 'right' }}>DIRECTION</div>
                                    </div>
                                    <hr
                                        style={{
                                            width: '100%',
                                            margin: 0,
                                            borderTop: '1px solid #cdcdcd',
                                            borderBottom: 'none',
                                            paddingBottom: 16,
                                            borderLeft: 'none',
                                            borderRight: 'none',
                                        }}
                                    />
                                    {circleToStrikes[lightningCircle.id].red.length > 0 && (
                                        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                                            {circleToStrikes[lightningCircle.id].red.map((strike) => {
                                                return (
                                                    <LightningStrikeRow key={strike.id}>
                                                        <div style={{ width: 50 }}>{strikeTimeToLabel(strike.properties.dateTime, selectedTime)}</div>
                                                        <div style={{ width: 75 }}>
                                                            <StrikeToType
                                                                strikeDateTime={strike.properties.dateTime}
                                                                strikeType={strike.properties.strike_type}
                                                                strikeKA={strike.properties.kA}
                                                                selectedTime={selectedTime}
                                                            />
                                                        </div>
                                                        <div style={{ width: 75, textAlign: 'right' }}>{distanceFromCenter(lightningCircle, strike)}</div>
                                                        <div style={{ width: 69, textAlign: 'right' }}>{strikeToAngle(lightningCircle, strike)}</div>
                                                    </LightningStrikeRow>
                                                );
                                            })}
                                        </div>
                                    )}
                                    {circleToStrikes[lightningCircle.id].orange.length > 0 && circleToStrikes[lightningCircle.id].red.length > 0 && (
                                        <div style={{ display: 'flex', flexDirection: 'row', paddingTop: 12, paddingBottom: 12 }}>
                                            <hr style={{ flex: 1, borderTop: '1px solid #cdcdcd', borderBottom: 'none' }} />
                                            <div style={{ flex: 1, textAlign: 'center', justifyContent: 'center', lineHeight: 1, fontWeight: '600' }}>
                                                {Duration.fromISO(lightningCircle.alertAge).as('minutes')}min
                                            </div>
                                            <hr style={{ flex: 1, borderTop: '1px solid #cdcdcd', borderBottom: 'none' }} />
                                        </div>
                                    )}
                                    {circleToStrikes[lightningCircle.id].orange.length > 0 && (
                                        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                                            {circleToStrikes[lightningCircle.id].orange.map((strike) => {
                                                return (
                                                    <LightningStrikeRow key={strike.id}>
                                                        <div style={{ width: 50 }}>{strikeTimeToLabel(strike.properties.dateTime, selectedTime)}</div>
                                                        <div style={{ width: 75 }}>
                                                            <StrikeToType
                                                                strikeDateTime={strike.properties.dateTime}
                                                                strikeType={strike.properties.strike_type}
                                                                strikeKA={strike.properties.kA}
                                                                selectedTime={selectedTime}
                                                            />
                                                        </div>
                                                        <div style={{ width: 75, textAlign: 'right' }}>{distanceFromCenter(lightningCircle, strike)}</div>
                                                        <div style={{ width: 69, textAlign: 'right' }}>{strikeToAngle(lightningCircle, strike)}</div>
                                                    </LightningStrikeRow>
                                                );
                                            })}
                                        </div>
                                    )}
                                </LightningList>
                            )}
                        </div>
                    );
                })}
            </div>
        </ChronologicalStrikesContainer>
    );
};

const StrikeToType = ({
    strikeDateTime,
    selectedTime,
    strikeType,
    strikeKA,
}: {
    strikeDateTime: string;
    selectedTime: string;
    strikeKA?: number;
    strikeType: string;
}) => {
    const strikesDateTime = strikeDateTime.endsWith('\r\n') ? strikeDateTime.slice(0, -2) : strikeDateTime;
    const colour = ageToColour(DateTime.fromISO(selectedTime).toUTC().diff(DateTime.fromISO(strikesDateTime).toUTC(), 'minutes').minutes);
    const icon =
        strikeKA === undefined ? <CloudCloud fill={colour} /> : strikeKA > 0 ? <CloudGroundPositive fill={colour} /> : <CloudGroundNegative fill={colour} />;
    return (
        <div
            style={{
                display: 'flex',
                flexDirection: 'row',
                gap: 8,
                alignItems: 'center',
            }}
        >
            {icon}
            {fixTypeCase(strikeType)}
        </div>
    );
};

const LightningStrikeRow = styled.div`
    display: flex;
    flex-direction: row;
    gap: 8px;
`;

const InteractiveHeader = styled.div`
    font-size: 14px;
    font-weight: bold;
    cursor: pointer;
    color: #172344;

    border-radius: 22px;
    padding: 8px 10px;
    padding-right: 4px;
    transition: background-color 0.1s ease-in-out;
    :hover {
        cursor: pointer;
        background-color: #658dff33;
    }
`;

const IconButton = styled.div`
    font-size: 16px;
    border-radius: 50%;
    width: 32px;
    height: 32px;
    display: flex;
    justify-content: center;
    align-items: center;
`;

//@ts-ignore range can be colours, ts is lies
const newestScale = scaleLinear().domain([0, 20]).range(['#F10707', '#FFE600']);
//@ts-ignore range can be colours, ts is lies
const middleScale = scaleLinear().domain([20, 40]).range(['#FFE600', '#39FF69']);
//@ts-ignore range can be colours, ts is lies
const oldestScale = scaleLinear().domain([40, 60]).range(['#39FF69', '#00A3FF']);

const ageToColour = (minutesOld: number) => {
    if (minutesOld < 20) return newestScale(minutesOld) as unknown as string;
    if (minutesOld < 40) return middleScale(minutesOld) as unknown as string;
    return oldestScale(minutesOld) as unknown as string;
};

const fixTypeCase = (type: string) => {
    if (type === 'GROUND') return 'Ground';
    if (type === 'CLOUD') return 'Cloud';
    return '-';
};

const strikeTimeToLabel = (strikeTime: string, selectedTime: string) => {
    const cleanedStrikeTime = strikeTime.endsWith('\r\n') ? strikeTime.slice(0, -2) : strikeTime;
    const difference = DateTime.fromISO(selectedTime).toUTC().diff(DateTime.fromISO(cleanedStrikeTime).toUTC(), 'seconds').toObject();
    if (difference.seconds === undefined) return '';

    if (Math.floor(difference.seconds) < 60) {
        return `${Math.floor(difference.seconds)}sec`;
    }
    return Math.round(Duration.fromObject(difference).as('minutes')) + 'min';
};

const distanceFromCenter = (lightningCircle: LightningCircle, strike: Strike) => {
    const unit = lightningCircle.unit;
    return (
        distance(lightningCircle.coordinates, strike.geometry.coordinates, {
            units: unitToTurfUnit(unit),
        }).toFixed(0) + unit
    );
};

const strikeToAngle = (lightningCircle: LightningCircle, strike: Strike) => {
    const angle = bearing(lightningCircle.coordinates, strike.geometry.coordinates);
    return convertDirectionToCardinal(angle);
};

const unitToTurfUnit = (unit: string) => {
    if (unit === 'km') return 'kilometers';
    if (unit === 'nm') return 'nauticalmiles';
    console.warn('Unknown unit: ' + unit);
    return 'kilometers';
};

const ChronologicalStrikesContainer = styled.div`
    border-radius: 4px;
    width: 320px;
`;

const LightningList = styled.div`
    display: flex;
    flex-direction: column;
    gap: 12;
    padding-left: 10px;
    padding-right: 10px;
`;

const CloudGroundPositive = ({ fill }: { fill: string }) => {
    return (
        <svg width='16' height='16' viewBox='0 0 16 16' fill='none' xmlns='http://www.w3.org/2000/svg'>
            <rect width='15' height='3' rx='1' transform='matrix(-1 1.0724e-06 1.11924e-06 -1 15.4998 9.5)' fill={fill} />
        </svg>
    );
};
const CloudGroundNegative = ({ fill }: { fill: string }) => {
    return (
        <svg width='16' height='16' viewBox='0 0 16 16' fill='none' xmlns='http://www.w3.org/2000/svg'>
            <path
                fillRule='evenodd'
                clipRule='evenodd'
                d='M14 9.5C14.5523 9.5 15 9.05229 15 8.5L15 7.5C15 6.94772 14.5523 6.5 14 6.5L9.49999 6.50001L9.5 2C9.5 1.44771 9.05228 1 8.5 1L7.5 1C6.94771 1 6.5 1.44772 6.5 2L6.49999 6.50001L2 6.50002C1.44772 6.50002 1 6.94773 1 7.50002L0.999999 8.50002C0.999998 9.0523 1.44771 9.50002 2 9.50002L6.49999 9.50001L6.49999 14C6.49999 14.5523 6.9477 15 7.49998 15L8.49998 15C9.05227 15 9.49999 14.5523 9.49999 14L9.49999 9.50001L14 9.5Z'
                fill={fill}
            />
        </svg>
    );
};
const CloudCloud = ({ fill }: { fill: string }) => {
    return (
        <svg width='16' height='16' viewBox='0 0 16 16' fill='none' xmlns='http://www.w3.org/2000/svg'>
            <path
                fillRule='evenodd'
                clipRule='evenodd'
                d='M13.6566 4.46463C14.0471 4.0741 14.0471 3.44094 13.6566 3.05041L12.9495 2.3433C12.559 1.95278 11.9258 1.95278 11.5353 2.3433L7.99993 5.87867L4.46439 2.34313C4.07387 1.9526 3.4407 1.9526 3.05018 2.34313L2.34307 3.05024C1.95255 3.44076 1.95255 4.07393 2.34307 4.46445L5.87861 8L2.34292 11.5357C1.95239 11.9262 1.95239 12.5594 2.34292 12.9499L3.05002 13.657C3.44055 14.0475 4.07371 14.0475 4.46423 13.657L7.99993 10.1213L11.5355 13.6568C11.926 14.0474 12.5591 14.0474 12.9497 13.6568L13.6568 12.9497C14.0473 12.5592 14.0473 11.9261 13.6568 11.5355L10.1212 8L13.6566 4.46463Z'
                fill={fill}
            />
        </svg>
    );
};

const LightningStrike = ({ fill }: { fill: string }) => {
    return (
        <svg width='16' height='16' viewBox='0 0 32 32' fill='none' xmlns='http://www.w3.org/2000/svg'>
            <path
                d='M21.5847 13.8333H18.1695L20.5803 10.3667L25 3H14.5533L7.12015 17.5167C6.91925 17.95 6.91925 18.8167 7.52195 19.25C7.72284 19.4667 7.92374 19.4667 8.12464 19.4667H11.9417L11.1381 27.7C11.1381 28.1333 11.1381 28.5667 11.5399 28.7833C11.7408 29 12.1426 29 12.3435 29C12.7453 29 12.9462 28.7833 13.1471 28.5667L22.5892 16C22.991 15.5667 22.991 14.9167 22.5892 14.2667C22.3883 13.8333 21.9865 13.8333 21.5847 13.8333Z'
                fill={fill}
            />
        </svg>
    );
};
